import Select, { type GroupBase, type StylesConfig } from 'react-select';
import type { Point } from '@shared/model/dataset';

export interface PointOption {
    value: string;
    label: string;
}

interface Props {
    id: string;
    placeholder: string;
    groups: Map<string, Point[]>;
    value: string | null;
    onChange: (pointId: string | null) => void;
}

const selectStyles: StylesConfig<PointOption, false, GroupBase<PointOption>> = {
    control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? 'var(--color-accent)' : 'var(--color-input-border)',
        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(2, 128, 146, 0.25)' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? 'var(--color-accent)' : 'var(--color-input-border)',
        },
    }),
    groupHeading: (base) => ({
        ...base,
        color: 'var(--color-dark)',
        fontWeight: 600,
        textTransform: 'none',
        fontSize: '0.875rem',
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--color-accent)'
            : state.isFocused
              ? 'var(--color-accent-25)'
              : 'transparent',
        color: state.isSelected ? '#fff' : 'inherit',
    }),
    menu: (base) => ({ ...base, zIndex: 20 }),
};

/** Searchable list of the (visible) points, grouped by coarse location. */
export function PointSelect({ id, placeholder, groups, value, onChange }: Props) {
    const options: GroupBase<PointOption>[] = [];
    let selected: PointOption | null = null;
    for (const [area, points] of groups) {
        const group = { label: area, options: [] as PointOption[] };
        for (const point of points) {
            if (point.hidden) continue;
            const option = { value: point.id, label: point.name };
            group.options.push(option);
            if (point.id === value) selected = option;
        }
        if (group.options.length > 0) options.push(group);
    }
    return (
        <Select<PointOption, false, GroupBase<PointOption>>
            inputId={id}
            instanceId={id}
            options={options}
            value={selected}
            placeholder={placeholder}
            onChange={(option) => onChange(option?.value ?? null)}
            isSearchable
            noOptionsMessage={() => 'Aucun point trouvé'}
            styles={selectStyles}
            theme={(theme) => ({
                ...theme,
                borderRadius: 6,
                colors: {
                    ...theme.colors,
                    primary: '#028092',
                    primary25: '#ecf6f7',
                    primary50: '#e0efea',
                },
            })}
        />
    );
}
